<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">Акты сверки</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <v-icon name="buttons/plus" :size="12"/>
          <b-spinner v-if="isBusy" small/>
          Новый акт
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          @submit="onSearch"
          hide-search-button
        />
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="reconciliation-acts"
            ref="reconciliation_acts"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="4"/>
            </template>

            <template #cell(create_date)="row">
              {{formatTableDate(row.item.create_date)}}
            </template>

            <template #cell(period)="row">
              {{formatTableDate(row.item.start_period)}} - {{formatTableDate(row.item.end_period)}}
            </template>

            <template #cell(action)="row">
              <b-btn variant="link" class="text-dark" @click="onEdit(row.item)">
                <v-icon name="buttons/edit" :size="15"/>
              </b-btn>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных актов оплаты.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новый акт прямо сейчас</p>
          <b-btn class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый акт
          </b-btn>
        </div>
      </div>
    </div>

    <DynamicTables
      storage-key="reconciliationAct_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>
import reconciliationActs from "../../network/reconciliationActs";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";

export default {
  name: "ReconciliationActs",
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, DynamicTables },
  mixins: [
    reconciliationActs,
    helpers, dateFormatter, pageSearchService
  ],
  data() {
    return {
      items: [],
      fields: [
        {
          key: 'id',
          label: 'id',
          class: 'reconciliation-acts-id',
          sortable: true,
          visible: true,
        },
        {
          key: 'create_date',
          label: "Дата создания",
          class: 'reconciliation-acts-date',
          sortable: true,
          visible: true,
        },
        {
          key: 'period',
          label: "Период",
          class: 'reconciliation-acts-period',
          sortable: true,
          visible: true,
        },
        {
          key: 'organization_name',
          label: "Организация",
          class: 'reconciliation-acts-organization',
          sortable: true,
          visible: true,
        },
        {
          key: 'customer_name',
          label: "Заказчик",
          class: 'reconciliation-acts-customer',
          sortable: true,
          visible: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: "Действия",
        class: 'reconciliation-acts-action'
      },
      isBusy: false,
      sortBy: 'id',
      sortDesc: true,
      filters: {
        query: '',
      },
      deleteRow: {
        id: null,
        name: ''
      },
    }
  },
  async created() {
    const query = this.$route.query;

    if (query && query.query) {
      this.filters.query = query.query;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    isFiltered() {
      return !!this.filters.query && this.filters.query.trim().length > 0;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getReconciliationActs(data);

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.isBusy = false;
    },
    onCreate() {
      this.$router.push({name: 'ReconciliationActAdd'}).catch(()=>{});
    },
    onRowClicked(item) {
      this.onEdit(item);
    },
    onEdit(item) {
      this.$router.push({name: 'ReconciliationActEdit', params: { id: item.id }}).catch(()=>{});
    },
  }
}
</script>

<style>
#reconciliation-acts td {
  vertical-align: middle;
}

.reconciliation-acts-id {
  padding-left: 10px !important;
  width: 60px;
  text-align: center;
}

.reconciliation-acts-date {
  width: 120px;
  text-align: center;
}

.reconciliation-acts-period {
  width: 200px;
  text-align: center;
}

.reconciliation-acts-organization {
  min-width: 200px;
  max-width: 200px;
}

.reconciliation-acts-customer {
  min-width: 200px;
  max-width: 200px;
}

.reconciliation-acts-action {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/ReconciliationActs/ReconciliationActs.vue